import { lazy, Suspense } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle2, Battery, Sun, Moon } from "lucide-react";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { riseFade } from "@/lib/motion";
import PillarSeo from "@/components/seo/PillarSeo";
import RelatedPillars from "@/components/seo/RelatedPillars";
import ShareButtons from "@/components/blog/ShareButtons";
import BookmarkButton from "@/components/blog/BookmarkButton";
import RelatedArticles from "@/components/blog/RelatedArticles";

const CheckinSimulator = lazy(() => import("@/components/marketing/CheckinSimulator"));

const META_TITLE = "MS Tiredness & Exhaustion: Energy Tracking That Predicts Your Day | Myelina Health";
const META_DESC =
  "MS tiredness and exhaustion don't follow ordinary rules. Myelina Health is an MS energy tracker and diary that turns a about-a-minute check-in into a one-sentence forecast for tomorrow. By invitation.";

const FAQS: { question: string; answer: string }[] = [
  {
    question: "What is MS energy tracking?",
    answer:
      "MS energy tracking is the practice of logging a small set of daily signals, sleep quality, perceived energy, stress, cognition, alongside optional wearable data, so that patterns emerge over time. The goal is not the log itself; it is using the log to anticipate energy windows instead of reacting to crashes.",
  },
  {
    question: "Is an MS diary the same thing?",
    answer:
      "Traditionally an MS diary is freeform notes about symptoms, triggers, and treatments. Myelina Health replaces the blank page with three quick questions and a structured forecast, so the diary is something you actually keep for months instead of weeks.",
  },
  {
    question: "How is this different from a generic fatigue tracker?",
    answer:
      "Generic fatigue trackers ask you to score tiredness 1–10 and show a line chart. Myelina Health is MS-specific: it uses PROMIS Fatigue-MS 8a, accounts for heat sensitivity and cognitive fog, and turns the data into a one-sentence forecast for tomorrow.",
  },
  {
    question: "Can I track energy without any wearable?",
    answer:
      "Yes. The about-a-minute morning check-in is the core. A wearable (Oura, Apple Watch, Fitbit, Garmin) sharpens prediction by adding HRV and sleep architecture, but the tracker is fully usable without one.",
  },
  {
    question: "How long until the predictions become useful?",
    answer:
      "Most users see a meaningful daily forecast after 2–3 weeks of consistent check-ins. The model improves continuously after that as it learns your personal baseline.",
  },
];

const MsEnergyTracking = () => {
  return (
    <>
      <PillarSeo
        slug="ms-energy-tracking"
        title={META_TITLE}
        description={META_DESC}
        faqs={FAQS}
        date="2026-05-17"
        image="/og/pillar-ms-energy-tracking.jpg"
        imageAlt="MS Energy Tracking. A calmer diary that maps your day | Myelina Health"
      />
    <div className="min-h-screen bg-background">
      <Navbar />
      <main>
        <section className="pt-32 pb-12 sm:pt-44 sm:pb-16">
          <div className="container mx-auto px-6 sm:px-10 lg:px-16">
            <motion.div initial="hidden" animate="visible" variants={riseFade} className="max-w-3xl mx-auto text-center space-y-6">
              <div className="eyebrow justify-center mx-auto">A calmer kind of MS diary</div>
              <h1 className="font-display text-foreground text-display-xl leading-[1.05]">
                MS tiredness, mapped, energy tracking that <span className="italic text-primary">predicts</span> your day.
              </h1>
              <p className="text-lg leading-[1.7] text-muted-foreground max-w-2xl mx-auto">
                MS tiredness and exhaustion don't behave like ordinary fatigue. A 60-second morning
                check-in becomes a one-sentence forecast for the day ahead, built around how
                multiple sclerosis fatigue actually behaves, not generic tiredness.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center pt-2">
                <Link to="/demo">
                  <Button variant="premium" size="lg" className="group w-full sm:w-auto px-8">
                    See the demo
                    <ArrowRight className="w-4 h-4 ml-1 transition-transform group-hover:translate-x-1" />
                  </Button>
                </Link>
                <Link to="/ms-fatigue-tracker">
                  <Button variant="outline" size="lg" className="w-full sm:w-auto px-8">
                    See the fatigue tracker
                  </Button>
                </Link>
              </div>
              <ShareButtons slug="ms-energy-tracking" title={META_TITLE} summary={META_DESC} className="justify-center mt-6" />
              <div className="flex justify-center mt-3">
                <BookmarkButton slug="ms-energy-tracking" title="MS Energy Tracking" category="Pillar" kind="pillar" />
              </div>
            </motion.div>
          </div>
        </section>

        <section className="container mx-auto px-6 sm:px-10 lg:px-16 py-16 sm:py-20">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-10% 0px" }} variants={riseFade} className="max-w-5xl mx-auto">
            <h2 className="font-display text-3xl sm:text-4xl text-foreground text-center mb-12">
              The three pieces of a useful MS energy log
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                { icon: Sun, title: "Morning signal", body: "Three quick questions in under a minute. Sleep, perceived energy, cognition. That's the floor of every prediction." },
                { icon: Battery, title: "Wearable layer (optional)", body: "HRV, resting heart rate, sleep architecture, skin temperature. Connect Oura, Apple Watch, Fitbit, or Garmin to shorten the learning curve." },
                { icon: Moon, title: "Evening reflection", body: "One sentence at night closes the loop, did the day match the forecast? That's how Myelina learns your personal pattern." },
              ].map((f) => (
                <Card key={f.title} className="p-6 sm:p-8 border-border/60 shadow-sm h-full">
                  <f.icon className="w-6 h-6 text-primary mb-3" />
                  <h3 className="font-display text-xl text-foreground mb-2">{f.title}</h3>
                  <p className="text-[15px] text-muted-foreground leading-relaxed">{f.body}</p>
                </Card>
              ))}
            </div>
          </motion.div>
        </section>

        <section className="container mx-auto px-6 sm:px-10 lg:px-16 py-12 sm:py-16">
          <Suspense fallback={null}>
            <CheckinSimulator variant="page" />
          </Suspense>
        </section>

        <section className="container mx-auto px-6 sm:px-10 lg:px-16 py-16 sm:py-20">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-10% 0px" }} variants={riseFade} className="max-w-3xl mx-auto space-y-6">
            <h2 className="font-display text-3xl sm:text-4xl text-foreground">
              Why a generic fatigue tracker doesn't cut it for MS
            </h2>
            <p className="text-[16px] leading-[1.8] text-foreground/85">
              Ordinary fatigue is linear: less sleep, more tired. MS fatigue isn't. Heat, hormonal cycle, infection, cognitive load, and yesterday's activity all bend the curve in ways a 1–10 slider can't capture. A proper MS energy tracker has to be MS-shaped from the first question.
            </p>
            <p className="text-[16px] leading-[1.8] text-foreground/85">
              Myelina Health is built on <strong>PROMIS Fatigue-MS 8a</strong>, the validated instrument your neurologist already trusts. Every entry contributes to a longitudinal record you can hand over at a visit, without becoming a homework assignment.
            </p>
            <h3 className="font-display text-2xl text-foreground mt-6">What's inside every morning</h3>
            <ul className="space-y-2 text-[15px] text-foreground/85">
              {["Daily check-in and energy logging", "Basic weekly trend summary", "Export your data any time"].map((item) => (
                <li key={item} className="flex items-start gap-2.5">
                  <CheckCircle2 className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        </section>

        <section className="container mx-auto px-6 sm:px-10 lg:px-16 py-16 sm:py-24" aria-labelledby="pillar-energy-faq">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-10% 0px" }} variants={riseFade} className="max-w-3xl mx-auto">
            <h2 id="pillar-energy-faq" className="font-display text-3xl sm:text-4xl text-foreground text-center mb-10">Frequently asked questions</h2>
            <dl className="divide-y divide-border/60">
              {FAQS.map((faq) => (
                <div key={faq.question} className="py-5">
                  <dt className="font-display italic text-foreground text-xl mb-2">{faq.question}</dt>
                  <dd className="text-[15px] text-muted-foreground leading-relaxed">{faq.answer}</dd>
                </div>
              ))}
            </dl>
          </motion.div>
        </section>

        <RelatedPillars
          intro="Related"
          links={[
            { to: "/ms-fatigue-tracker", label: "MS fatigue tracker" },
            { to: "/living-with-ms", label: "living with MS" },
          ]}
        />

        <RelatedArticles topics={["energy tracking", "ms tiredness", "exhaustion", "wearable"]} featuredSlugs={["best-ms-tracking-apps-2026", "ms-heat-sensitivity-energy", "spoon-theory-vs-energy-budgeting-ms"]} />
        <section className="bg-secondary py-20 sm:py-28">
          <div className="container mx-auto px-6 sm:px-10 lg:px-16 text-center">
            <h2 className="font-display text-3xl sm:text-4xl text-foreground mb-4">
              Start an energy diary that <span className="italic text-primary">earns its keep.</span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-xl mx-auto mb-8">
              Three questions, no account. See the windows Myelina would surface for tomorrow.
            </p>
            <Link to="/demo">
              <Button variant="premium" size="lg" className="group">
                See the demo
                <ArrowRight className="w-4 h-4 ml-1 transition-transform group-hover:translate-x-1" />
              </Button>
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
    </>
  );
};

export default MsEnergyTracking;
